import { FC, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { upperFirst } from "lodash";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import { DriverFlowBottomButton } from "../blocks";
import { DsDataDisplay, DsDivider, DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useJobQuery } from "../data/queries/jobQueries";
import {
  ServiceQueryKeys,
  useServiceQuery,
} from "../data/queries/serviceQueries";
import { TaskQueryKeys, useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingJunkLoadAtom } from "../stores/atoms";
import { setJunkLoadInProgress } from "../utils/service";
import { CustomerNameText } from "./CustomerNameText";
import { DriverTaskPicture } from "./DriverTaskPicture";

type DriverJunkLoadReviewScreenPropsType = JobServiceTaskBaseType & {
  onBottomButtonPress?: () => void;
};

const DriverJunkLoadReviewScreen: FC<DriverJunkLoadReviewScreenPropsType> = ({
  jobId,
  serviceId,
  taskId,
  onBottomButtonPress,
}) => {
  const { t } = useTranslator();

  const queryClient = useQueryClient();

  const setDriverIsReviewingJunkLoad = useSetAtom(
    isDriverReviewingJunkLoadAtom,
  );

  const { data: jobData } = useJobQuery({ jobId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  // Get the most recent junk load
  const junkLoadData =
    serviceData?.junkLoads?.[serviceData.junkLoads.length - 1];

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables: updateTaskVariables,
  } = useUpdateTaskMutation();

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  useEffect(() => {
    setDriverIsReviewingJunkLoad(true);
  }, [setDriverIsReviewingJunkLoad]);

  if (!jobData || !taskData) {
    return null;
  }

  const onFinishPress = async () => {
    const promises = [];

    promises.push(
      updateService({
        jobId,
        serviceId,
        needToDump: true,
        hasDumped: false,
        driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
      }),
    );

    promises.push(
      updateTask({
        jobId,
        serviceId,
        taskId,
        needToDump: true,
        hasDumped: false,
        driverFlowStatus: TaskDriverStatusEnum.DRIVING_TO_DUMP,
      }),
    );

    await Promise.allSettled(promises);

    await queryClient.invalidateQueries({ queryKey: TaskQueryKeys.all });
    await queryClient.invalidateQueries({ queryKey: ServiceQueryKeys.all });

    setDriverIsReviewingJunkLoad(false);

    onBottomButtonPress?.();
  };

  const loadNumber = Number(junkLoadData?.loadNumber ?? 1);

  const goBackTo = (driverFlowStatus: TaskDriverStatusEnum) => {
    const currentJunkLoads = serviceData?.junkLoads;
    const currentJunkLoadIndex = currentJunkLoads?.findIndex(
      junkLoad => junkLoad.loadNumber === loadNumber,
    );

    if (
      currentJunkLoads &&
      currentJunkLoadIndex !== undefined &&
      currentJunkLoadIndex !== -1
    ) {
      const updatedJunkLoads = setJunkLoadInProgress(
        serviceData,
        currentJunkLoadIndex,
      );

      updateService({
        jobId,
        serviceId,
        driverFlowStatus,
        junkLoads: updatedJunkLoads,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus,
      });
    }
  };

  const isCompleting =
    isUpdatingService &&
    isUpdatingTask &&
    updateTaskVariables.needToDump === true;

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <div className="flex flex-grow w-full flex-col">
          <DsText variant="pageTitle">
            {t("driver_load_review_screen.title", { loadNumber })}
          </DsText>
          <DsText variant="dataDisplayTitle" className="text-black">
            {t("driver_load_review_screen.subtitle", { loadNumber })}
          </DsText>
          <DsDivider className="my-4" />

          <DsDataDisplay
            title="driver_task_details.customer"
            data={
              <CustomerNameText
                jobId={jobId}
                serviceId={serviceId}
                variant="dataDisplayValue"
                className="font-bold"
              />
            }
          />

          <DsDataDisplay
            title="driver_task_review_screen.before_picture"
            dataClassName="flex"
            data={
              <DriverTaskPicture
                field="preJunkRemovalPhoto"
                jobId={jobId}
                serviceId={serviceId}
                junkLoadIndex={loadNumber - 1}
              />
            }
          />

          <DsDataDisplay
            title="driver_task_review_screen.load_size"
            dataClassName="font-bold"
            data={junkLoadData?.loadSize}
            onEditClick={() => goBackTo(TaskDriverStatusEnum.FILLING_LOAD_SIZE)}
            isLoading={Boolean(
              updateTaskVariables?.driverFlowStatus ===
                TaskDriverStatusEnum.FILLING_LOAD_SIZE,
            )}
          />
          <DsDataDisplay
            title="driver_task_review_screen.location_access"
            dataClassName="font-bold"
            data={upperFirst(junkLoadData?.locationAccess?.toLowerCase())}
            onEditClick={() =>
              goBackTo(TaskDriverStatusEnum.FILLING_LOCATION_ACCESS)
            }
            isLoading={Boolean(
              updateTaskVariables?.driverFlowStatus ===
                TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
            )}
          />
          <DsDataDisplay
            title="driver_task_review_screen.after_picture"
            dataClassName="flex"
            data={
              <DriverTaskPicture
                field="postJunkRemovalPhoto"
                jobId={jobId}
                serviceId={serviceId}
                junkLoadIndex={loadNumber - 1}
              />
            }
          />
          <DsDataDisplay
            title="driver_load_review_screen.extra_load"
            data={t("yes")}
          />
        </div>
        <DriverFlowBottomButton
          nextButtonTitle="driver_load_review_screen.next_button"
          isNextButtonLoading={isCompleting}
          onNextButtonClick={() => {
            void onFinishPress();
          }}
        />
      </div>
    </>
  );
};

export { DriverJunkLoadReviewScreen };
